import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import ReactApexChart from 'react-apexcharts';
import { peopleApi, assetsApi, registersApi, riskApi, listApi } from '../services/api';
import { useAuth } from '../hooks/useAuth';

type Register = {
  registerId: string;
  name?: string;
  template?: string;
  status?: string;
  createdAt?: string;
};

type Risk = {
  riskId: string;
  createdAt?: string;
  fields?: Record<string, any>;
};

const getTemplateLabel = (t?: string) => {
  const map: Record<string, string> = {
    simple: 'Simple', nist: 'NIST RMF', iso27001: 'ISO 27001',
    coso: 'COSO ERM', comprehensive: 'Comprehensive', blank: 'Blank',
  };
  return t ? (map[t] || t) : '—';
};

const getTemplateTone = (t?: string) => {
  const map: Record<string, string> = {
    iso27001: 'primary', nist: 'info', coso: 'warning',
    comprehensive: 'info', simple: 'neutral', blank: 'neutral',
  };
  return (t && map[t]) || 'neutral';
};

const donutOptions = (
  labels: string[],
  colors: string[],
  center: { value: number; label: string; color: string },
): ApexCharts.ApexOptions => ({
  chart: { type: 'donut' },
  labels,
  colors,
  stroke: { width: 0 },
  dataLabels: { enabled: false },
  legend: { position: 'bottom', fontSize: '13px' },
  plotOptions: {
    pie: {
      donut: {
        size: '68%',
        labels: {
          show: true,
          total: {
            show: true,
            showAlways: true,
            label: center.label,
            color: center.color,
            fontSize: '13px',
            fontWeight: 500,
            formatter: () => String(center.value),
          },
        },
      },
    },
  },
  tooltip: { y: { formatter: (v: number) => `${v} risks` } },
});

/** Radial percentage gauge for the Framework Readiness cluster — same stroke-dasharray
 * technique as CatalogPage.tsx's MetricRing, sized for a 4-up grid of at-a-glance percentages. */
const FrameworkGauge: React.FC<{ pct: number; color: string }> = ({ pct, color }) => {
  const size = 72;
  const stroke = 7;
  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dash = (Math.max(0, Math.min(100, pct)) / 100) * circumference;
  return (
    <div className="np-gauge">
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="var(--np-border)" strokeWidth={stroke} />
        <circle
          cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color} strokeWidth={stroke}
          strokeDasharray={`${dash} ${circumference}`} strokeLinecap="round"
        />
      </svg>
      <span className="np-gauge-value" style={{ color }}>{pct}%</span>
    </div>
  );
};

const getCtrlField = (c: any, key: string): string =>
  String(c?.fields?.[key] ?? c?.[key] ?? '');


export const SecurityDashboard: React.FC = () => {
  const { user } = useAuth();
  const displayName = (user as any)?.name || (user as any)?.email?.split('@')[0] || 'there';

  const { data, isLoading } = useQuery({
    queryKey: ['dashboard'],
    queryFn: async () => {
      const [people, assets, registers, risks, controls] = await Promise.all([
        peopleApi.getAll(),
        assetsApi.getAll(),
        registersApi.getAll(),
        riskApi.listAll(),
        listApi.controls(),
      ]);
      return {
        people: people as Array<{ createdAt?: string }>,
        assets: assets as Array<{ createdAt?: string }>,
        registers: (registers || []) as Register[],
        risks: (Array.isArray(risks) ? risks : []) as Risk[],
        controls: (Array.isArray(controls) ? controls : []) as any[],
      };
    },
  });

  const people = data?.people || [];
  const assets = data?.assets || [];
  const risks = data?.risks || [];
  const registers = data?.registers || [];
  const controls = data?.controls || [];

  const riskDistribution = useMemo(() => {
    const counts = { Critical: 0, High: 0, Medium: 0, Low: 0, Unclassified: 0 };
    risks.forEach((r) => {
      const p = String(r.fields?.risk_priority || r.fields?.inherent_risk || '').toLowerCase();
      if (p.includes('critical')) counts.Critical++;
      else if (p.includes('high')) counts.High++;
      else if (p.includes('medium') || p.includes('moderate')) counts.Medium++;
      else if (p.includes('low')) counts.Low++;
      else counts.Unclassified++;
    });
    return counts;
  }, [risks]);

  const personNameMap = useMemo(() => {
    const m = new Map<string, string>();
    people.forEach((p: any) => {
      const full = `${p.firstName || ''} ${p.lastName || ''}`.trim();
      const name = full || p.personName || p.personId;
      if (p.personId) m.set(p.personId, name);
    });
    return m;
  }, [people]);

  const resolveOwner = (raw: string): string => {
    if (!raw) return '';
    return personNameMap.get(raw) || raw;
  };

  const registerRiskCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    risks.forEach((r) => {
      const rid = (r as any).registerId || 'unknown';
      counts[rid] = (counts[rid] || 0) + 1;
    });
    return counts;
  }, [risks]);

  const recentRisks = useMemo(() => risks.slice(0, 5), [risks]);

  const recentRegisters = registers.slice(0, 5);

  const frameworkReadiness = useMemo(() => {
    const fw: Record<string, { total: number; implemented: number }> = {};
    controls.forEach((c: any) => {
      const framework = getCtrlField(c, 'framework') || 'Other';
      const status = getCtrlField(c, 'implementation_status').toLowerCase();
      if (!fw[framework]) fw[framework] = { total: 0, implemented: 0 };
      fw[framework].total++;
      if (status.includes('implement') || status === 'active' || status === 'compliant') {
        fw[framework].implemented++;
      }
    });
    return Object.entries(fw).map(([name, { total, implemented }]) => ({
      name,
      total,
      implemented,
      pct: total > 0 ? Math.round((implemented / total) * 100) : 0,
    }));
  }, [controls]);

  const FRAMEWORK_GAUGE_CAP = 4;
  const worstFrameworksFirst = useMemo(
    () => [...frameworkReadiness].sort((a, b) => a.pct - b.pct),
    [frameworkReadiness],
  );
  const shownFrameworks = worstFrameworksFirst.slice(0, FRAMEWORK_GAUGE_CAP);
  const hiddenFrameworkCount = worstFrameworksFirst.length - shownFrameworks.length;

  const statCards = [
    { label: 'People', value: people.length, sub: 'Team members', icon: 'ri-user-line', iconBg: 'bg-label-primary' },
    { label: 'Assets', value: assets.length, sub: 'Tracked assets', icon: 'ri-database-line', iconBg: 'bg-label-info' },
    { label: 'Risk Registers', value: registers.length, sub: 'Active registers', icon: 'ri-folders-line', iconBg: 'bg-label-warning' },
    { label: 'Total Risks', value: risks.length, sub: `${riskDistribution.Critical + riskDistribution.High} need attention`, icon: 'ri-alert-line', iconBg: 'bg-label-danger' },
  ];

  // Derived from real theme tokens (color-mix, same technique nimbus-premium.css's own
  // --np-surface-2 uses) rather than hardcoded hex, so this respects the dark-mode toggle -
  // and keyed by name rather than a parallel positional array, since donutKeys is filtered to
  // only the categories actually present, which previously misaligned index-to-color whenever
  // a category in the middle of the fixed list was filtered out.
  const DONUT_COLOR: Record<string, string> = {
    Critical: 'var(--bs-danger)',
    High: 'color-mix(in srgb, var(--bs-danger) 55%, var(--bs-warning))',
    Medium: 'var(--bs-warning)',
    Low: 'var(--bs-success)',
    Unclassified: 'var(--bs-secondary)',
  };
  const donutKeys = (['Critical', 'High', 'Medium', 'Low', 'Unclassified'] as const).filter((k) => riskDistribution[k] > 0);
  const donutSeries = donutKeys.map((k) => riskDistribution[k]);
  const donutColors = donutKeys.map((k) => DONUT_COLOR[k]);

  // The center number answers "is anything on fire", not a restatement of the total already
  // shown in the Total Risks stat card above.
  const needAttention = riskDistribution.Critical + riskDistribution.High;
  const donutCenter = {
    value: needAttention,
    label: needAttention > 0 ? 'Need attention' : 'All clear',
    color: needAttention > 0 ? 'var(--bs-danger-text-emphasis)' : 'var(--bs-success-text-emphasis)',
  };

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y d-flex justify-content-center align-items-center" style={{ minHeight: 400 }}>
        <div className="spinner-border text-primary" role="status"><span className="visually-hidden">Loading…</span></div>
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">

      {/* Welcome / Hero Row */}
      <div className="np-grid np-grid-8-4">
        <div className="np-card">
          <div className="np-card-body d-flex align-items-start justify-content-between">
            <div className="flex-grow-1">
              <h2 style={{ marginBottom: 4, fontSize: '1.375rem', fontWeight: 700, color: 'var(--bs-heading-color)', lineHeight: 1.3 }}>Welcome back, {displayName}! 👋</h2>
              <p className="np-sub" style={{ marginBottom: '1rem' }}>Here's what's happening across your GRC platform today.</p>
              <div className="row g-3">
                {[
                  { count: riskDistribution.Critical + riskDistribution.High, label: 'High / Critical', tone: 'danger', icon: 'ri-alert-line' },
                  { count: riskDistribution.Medium, label: 'Medium Risk', tone: 'warning', icon: 'ri-error-warning-line' },
                  { count: riskDistribution.Low, label: 'Low Risk', tone: 'success', icon: 'ri-checkbox-circle-line' },
                  { count: riskDistribution.Unclassified, label: 'Unclassified', tone: 'neutral', icon: 'ri-question-line' },
                ].map((item) => (
                  <div key={item.label} className="col-6 col-md-3">
                    <div className="d-flex align-items-center gap-2">
                      <span className={`np-icon-badge np-tone-${item.tone}`}><i className={item.icon}></i></span>
                      <div>
                        <div className="np-tabular" style={{ fontWeight: 600, lineHeight: 1 }}>{item.count}</div>
                        <small className="np-hint">{item.label}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-4 d-flex gap-2 flex-wrap">
                <Link to="/registers" className="np-btn np-btn-primary np-btn-sm"><i className="ri-add-line"></i>New Register</Link>
                <Link to="/risks" className="np-btn np-btn-ghost np-btn-sm"><i className="ri-error-warning-line"></i>View Risks</Link>
                <Link to="/reports" className="np-btn np-btn-ghost np-btn-sm"><i className="ri-bar-chart-box-line"></i>Reports</Link>
              </div>
            </div>
            <i className="ri-shield-check-line ms-3 d-none d-md-block" style={{ fontSize: '3rem', color: 'var(--bs-primary)', opacity: 0.25 }}></i>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>Quick Actions</h2></div>
          <div className="np-card-body d-flex flex-column gap-2">
            <Link to="/people" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-user-add-line"></i>Add Team Member</Link>
            <Link to="/assets" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-add-line"></i>Add Asset</Link>
            <Link to="/registers" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-folder-add-line"></i>Create Register</Link>
            <Link to="/readiness" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-award-line"></i>ISMS Journey</Link>
            <Link to="/reports" className="np-btn np-btn-ghost np-btn-sm w-100 justify-content-start"><i className="ri-bar-chart-box-line"></i>Gap Analysis</Link>
          </div>
        </div>
      </div>

      {/* Stat cards */}
      <div className="np-grid np-grid-4">
        {statCards.map((card) => (
          <div key={card.label} className="np-card">
            <div className="np-card-body np-stat">
              <span className="np-stat-icon np-tone-primary"><i className={card.icon}></i></span>
              <p className="np-stat-label">{card.label}</p>
              <p className="np-stat-value np-tabular">{card.value}</p>
              <p className="np-stat-trend" style={{ color: card.label === 'Total Risks' && (riskDistribution.Critical + riskDistribution.High) > 0 ? 'var(--bs-danger)' : 'var(--bs-success)' }}>
                {card.label === 'Total Risks' && (riskDistribution.Critical + riskDistribution.High) > 0
                  ? <><i className="ri-alert-line"></i>{card.sub}</>
                  : <><i className="ri-arrow-up-line"></i>{card.sub}</>}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Charts row */}
      <div className="np-grid np-grid-12-4-8">
        <div className="np-card">
          <div className="np-card-head"><h2>Risk Distribution</h2></div>
          <div className="np-card-body">
            {risks.length === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-pie-chart-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>No risk data yet
              </div>
            ) : (
              <ReactApexChart type="donut" height={300} series={donutSeries}
                options={donutOptions(donutKeys, donutColors.slice(0, donutKeys.length), donutCenter)} />
            )}
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head"><h2>Framework Readiness</h2></div>
          <div className="np-card-body">
            {shownFrameworks.length === 0 ? (
              <div className="text-center py-4 np-sub">
                <i className="ri-shield-check-line d-block mb-2" style={{ fontSize: '2rem', opacity: 0.5 }}></i>
                No control matrix data yet — add a control matrix to see framework readiness
              </div>
            ) : (
              <>
                <div className="np-gauge-cluster">
                  {shownFrameworks.map(({ name, total, implemented, pct }) => {
                    const color = pct >= 80 ? 'var(--bs-success)' : pct >= 50 ? 'var(--bs-warning)' : 'var(--bs-info)';
                    return (
                      <div key={name} className="np-gauge-card">
                        <FrameworkGauge pct={pct} color={color} />
                        <span className="np-gauge-name">{name}</span>
                        <span className="np-gauge-sub">{implemented} of {total}</span>
                      </div>
                    );
                  })}
                </div>
                {hiddenFrameworkCount > 0 && (
                  <div className="np-gauge-viewall">
                    <Link to="/control-matrices">View all {worstFrameworksFirst.length} frameworks →</Link>
                    <span className="np-hint">{shownFrameworks[0].name} ({shownFrameworks[0].pct}%) shown first — worst readiness, not creation order</span>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>

      {/* Recent risks + registers */}
      <div className="np-grid np-grid-2">
        <div className="np-card">
          <div className="np-card-head">
            <h2>Recent Risks</h2>
            <Link to="/risks" className="np-btn np-btn-ghost np-btn-sm">View All</Link>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Risk</th><th>Priority</th><th>Owner</th></tr></thead>
                <tbody>
                  {recentRisks.length === 0 ? (
                    <tr><td colSpan={3} className="text-center np-sub py-4"><i className="ri-shield-line me-1"></i>No risks found</td></tr>
                  ) : recentRisks.map((r) => {
                    const title = String(r.fields?.title || r.fields?.risk_title || r.fields?.name || '(Untitled)');
                    const priority = String(r.fields?.priority || r.fields?.risk_priority || r.fields?.inherent_risk || '');
                    const owner = resolveOwner(String(r.fields?.owner || ''));
                    const p = priority.toLowerCase();
                    const pTone = p.includes('critical') || p.includes('high') ? 'danger' : p.includes('medium') || p.includes('moderate') ? 'warning' : p.includes('low') ? 'success' : 'neutral';
                    return (
                      <tr key={(r as any).riskId}>
                        <td><Link to={`/risks/${(r as any).riskId}`} className="np-row-title">{title}</Link></td>
                        <td>{priority ? <span className={`np-chip np-tone-${pTone}`}>{priority}</span> : <span className="np-sub">—</span>}</td>
                        <td>{owner || <span className="np-sub">—</span>}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <div className="np-card">
          <div className="np-card-head">
            <h2>Risk Registers</h2>
            <Link to="/registers" className="np-btn np-btn-ghost np-btn-sm">View All</Link>
          </div>
          <div className="np-card-body" style={{ padding: 0 }}>
            <div className="np-table-wrap">
              <table className="np-table">
                <thead><tr><th>Name</th><th>Template</th><th>Risks</th><th>Status</th></tr></thead>
                <tbody>
                  {recentRegisters.length === 0 ? (
                    <tr><td colSpan={4} className="text-center np-sub py-4"><i className="ri-file-list-3-line me-1"></i>No registers found</td></tr>
                  ) : recentRegisters.map((r) => (
                    <tr key={r.registerId}>
                      <td><Link to={`/risks?registerId=${encodeURIComponent(r.registerId)}`} className="np-row-title">{r.name || r.registerId}</Link></td>
                      <td><span className={`np-chip np-tone-${getTemplateTone(r.template)}`}>{getTemplateLabel(r.template)}</span></td>
                      <td className="np-tabular">{registerRiskCounts[r.registerId] || 0}</td>
                      <td>{r.status === 'active' ? <span className="np-chip np-tone-success">Active</span> : <span className="np-chip np-tone-neutral">Archived</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

